import '../styles/adminOnboarding.css';
import { createFacilityInvitation } from "../services/access/facilityInvitations";
import { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

import { db } from "../firebase";

import {
  normalizeId,
} from "../security/permissions";

export default function AdminInviteConsole({
  selectedTenant,
  selectedFacilityId,
  invites = [],
}) {
  const [showForm, setShowForm] = useState(false);

 const [email, setEmail] = useState("");
const [name, setName] = useState("");
const [phone, setPhone] = useState("");

const [accountType, setAccountType] = useState("worker");
const [position, setPosition] = useState("LVN");
const [adminRole, setAdminRole] = useState("operations_admin");

const [source, setSource] = useState("manual_invite"); 

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");


const pendingInvites = useMemo(() => {
  return (invites || []).filter((invite) => {
    if (invite?.archived === true) return false;

    const emailLower = String(
      invite?.emailLower || invite?.email || ""
    )
      .trim()
      .toLowerCase();

    const role = String(
      invite?.platformRole || invite?.role || ""
    ).toLowerCase();

    const workerType = String(
      invite?.position ||
        invite?.primaryRole ||
        invite?.workerType ||
        invite?.roleType ||
        ""
    ).toLowerCase();

    const inviteStatus = String(
      invite?.inviteStatus ||
        invite?.invitationStatus ||
        invite?.status ||
        ""
    ).toLowerCase();

return ["invited", "sent", "pending"].includes(inviteStatus);
  });
}, [invites]);

  async function handleInvite() {
    try {
      setSaving(true);
      setMessage("");

      const emailLower = email.trim().toLowerCase();

      if (!emailLower) {
  throw new Error("Email is required.");
}

const selectedRole =
  accountType === "facility_planner"
    ? "facility_planner"
    : accountType === "facility_pulse"
    ? "facility_admin_viewer"
    : accountType === "internal_admin"
    ? adminRole
    : "worker";

const selectedPosition =
  accountType === "worker"
    ? position
    : accountType === "facility_planner"
    ? "FACILITY_PLANNER"
    : accountType === "facility_pulse"
    ? "FACILITY_ADMIN_VIEWER"
    : adminRole.toUpperCase();

const requiresFacility =
  accountType === "facility_planner" ||
  accountType === "facility_pulse";

/*
 * =========================================================
 * CANONICAL INVITE TENANT / FACILITY SCOPE
 * =========================================================
 */

const cleanTenantId =
  normalizeId(
    selectedTenant
  );

if (
  !cleanTenantId ||
  cleanTenantId === "all"
) {
  throw new Error(
    "Select a specific tenant before creating this invite."
  );
}

const cleanFacilityId =
  normalizeId(
    selectedFacilityId
  );

const hasSelectedFacility =
  Boolean(cleanFacilityId) &&
  cleanFacilityId !== "all";

/*
 * Facility operational accounts cannot exist without
 * a concrete facility.
 *
 * Workers may legitimately be invited without one.
 */
if (
  requiresFacility &&
  !hasSelectedFacility
) {
  throw new Error(
    "Select a specific facility for this account type."
  );
}

/*
 * When a facility is supplied, prove that it exists
 * and belongs to the selected tenant before writing
 * the invitation.
 */
if (hasSelectedFacility) {
  const facilityRef = doc(
    db,
    "facilityProfiles",
    cleanFacilityId
  );

  const facilitySnap =
    await getDoc(
      facilityRef
    );

  if (!facilitySnap.exists()) {
    throw new Error(
      `Facility profile not found: ${cleanFacilityId}`
    );
  }

  const facilityData =
    facilitySnap.data() || {};

  const facilityTenantId =
    normalizeId(
      facilityData?.tenantId ||
        facilityData?.tenantKey ||
        ""
    );

  if (!facilityTenantId) {
    throw new Error(
      `Facility ${cleanFacilityId} is missing tenantId.`
    );
  }

  if (
    facilityTenantId !==
    cleanTenantId
  ) {
    throw new Error(
      `Facility ${cleanFacilityId} does not belong to tenant ${cleanTenantId}.`
    );
  }
}

/*
 * Internal Administrators are tenant-level accounts.
 *
 * Workers may have a facility when specifically
 * recruited for one, but facility assignment remains
 * optional during registration.
 */
const inviteFacilityId =
  accountType === "internal_admin"
    ? ""
    : hasSelectedFacility
    ? cleanFacilityId
    : "";

const inviteFacilityIds =
  inviteFacilityId
    ? [inviteFacilityId]
    : [];

if (requiresFacility) {
  await createFacilityInvitation({ email: emailLower, name, phone, role: selectedRole, tenantId: cleanTenantId, facilityId: cleanFacilityId });
} else {
await setDoc(
        doc(db, "invites", emailLower),
        {
          email: emailLower,
          emailLower,

          name: name.trim(),
          phone: phone.trim(),

          accountType,

role: selectedRole,
platformRole: selectedRole,

primaryRole: selectedPosition,
position: selectedPosition,
workerType:
  accountType === "worker" ? selectedPosition : null,

tenantId:
  cleanTenantId,

facilityId:
  inviteFacilityId || null,

primaryFacilityId:
  inviteFacilityId || null,

facilityIds:
  inviteFacilityIds,

allowedFacilityIds:
  inviteFacilityIds,
         
          active: true,
          disabled: false,

          inviteStatus: "sent",
          invitationStatus: "sent",
          status: "sent",

          registrationStatus: "invited",

          onboardingStatus:
            accountType === "worker"
              ? "not_started"
              : "not_applicable",

          complianceStatus:
            accountType === "worker"
              ? "not_started"
              : "not_applicable",

          workerStatus:
            accountType === "worker"
              ? "invited"
              : "not_applicable",

          archived: false,

          profileCompleted: accountType !== "worker",
          documentsSubmitted: accountType !== "worker",
          documentsApproved: accountType !== "worker",
          readyForShifts: false,

          yearsExperience: 0,
          travelRadiusMiles: 25,
          hasTransportation: false,

          availableDays: false,
          availableEvenings: false,
          availableNights: false,
          availableWeekends: false,
          preferredDays: [],

          source,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
}

      setEmail("");
      setName("");
      setPhone("");

      setAccountType("worker");
      setPosition("LVN");
      setAdminRole("operations_admin");

      setSource("manual_invite");
      setShowForm(false);
      setMessage("Invite created and added to console.");
    } catch (err) {
      console.error(err);
      setMessage(err?.message || "Invite failed.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={accountType === 'worker' ? 'admin-onboarding ao-worker-invite' : undefined} style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 950 }}>Invite Console</div>
          <div style={{ marginTop: 4, color: "#64748b", fontWeight: 700 }}>
            Pending invitations for workers, facility accounts, and internal administrators.
          </div>
        </div>

        <button type="button" onClick={() => setShowForm((v) => !v)} style={buttonStyle}>
          {showForm ? "Close" : "New Invite"}
        </button>
      </div>

      {message ? <div style={{ fontWeight: 800 }}>{message}</div> : null}

      {showForm ? (
        <div style={{ display: "grid", gap: 10 }}>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" style={inputStyle} />
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" style={inputStyle} />
<input
  value={phone}
  onChange={(e) => setPhone(e.target.value)}
  placeholder="Phone"
  style={inputStyle}
/>

<select
  value={accountType}
  onChange={(e) => {
    const nextType = e.target.value;
    setAccountType(nextType);

    if (nextType === "worker") {
      setPosition("LVN");
    }

    if (nextType === "facility_planner") {
      setPosition("FACILITY_PLANNER");
    }

    if (nextType === "facility_pulse") {
      setPosition("FACILITY_ADMIN_VIEWER");
    }
  }}
  style={inputStyle}
>
  <option value="worker">Worker</option>
  <option value="facility_planner">Facility Planner</option>
  <option value="facility_pulse">Facility Pulse Viewer</option>
  <option value="internal_admin">Internal Administrator</option>
</select>

{accountType === "worker" ? (
  <select
    value={position}
    onChange={(e) => setPosition(e.target.value)}
    style={inputStyle}
  >

            <option value="CNA">CNA</option>
            <option value="LVN">LVN</option>
            <option value="RN">RN</option>
            <option value="MED_AIDE">Med Aide</option>
              <option value="CAREGIVER">Caregiver</option>
  </select>
) : null}

{accountType === "facility_planner" ? (
  <div style={inputStyle}>Role: Facility Planner</div>
) : null}

{accountType === "facility_pulse" ? (
  <div style={inputStyle}>Role: Facility Admin Viewer</div>
) : null}

{accountType === "internal_admin" ? (
  <select
    value={adminRole}
    onChange={(e) => setAdminRole(e.target.value)}
    style={inputStyle}
  >
    <option value="owner_admin">Owner Administrator</option>
    <option value="operations_admin">Operations Administrator</option>
    <option value="finance_admin">Finance Administrator</option>
  </select>
) : null}

<select value={source}
          
          onChange={(e) => setSource(e.target.value)} style={inputStyle}>
            <option value="manual_invite">Manual Invite</option>
            <option value="facebook">Facebook</option>
            <option value="linkedin">LinkedIn</option>
            <option value="indeed">Indeed</option>
            <option value="referral">Referral</option>
            <option value="qr_code">QR Code</option>
            <option value="website">Website</option>
            <option value="facility_campaign">Facility Campaign</option>
          </select>

          <button type="button" onClick={handleInvite} disabled={saving} style={primaryButtonStyle}>
            {saving ? "Creating..." : "Create Invite"}
          </button>
        </div>
      ) : null}

      <div style={{ fontWeight: 950 }}>
        Pending Account Invites: {pendingInvites.length}
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        {pendingInvites.length === 0 ? (
          <div style={{ color: "#64748b", fontWeight: 800 }}>
            No pending invites.
          </div>
        ) : (
          pendingInvites.map((i) => (
            <div key={i.id || i.email} style={rowStyle}>
              <div>
                <div style={{ fontWeight: 950 }}>{i.email}</div>
                <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>
                  {i.name || "No name"} •{" "}
                  {i.accountType === "facility_planner"
                    ? "Facility Planner"
                    : i.accountType === "facility_pulse"
                      ? "Facility Pulse Viewer"
                      : i.accountType === "internal_admin"
                        ? String(i.platformRole || i.role || "Internal Administrator")
                          .replaceAll("_", " ")
                          .replace(/\b\w/g, (letter) => letter.toUpperCase())
                        : i.position || i.primaryRole || "Worker"}{" "}
                  • {i.source || "manual_invite"}
                </div>
              </div>

              <div style={statusStyle}>
                {String(
                  i.inviteStatus ||
                  i.invitationStatus ||
                  i.status ||
                  "sent"
                ).toUpperCase()}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const cardStyle = {
  padding: 16,
  borderRadius: 16,
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  display: "grid",
  gap: 12,
};

const inputStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 12px",
  fontWeight: 800,
};

const buttonStyle = {
  border: "1px solid #d8e1ea",
  background: "#fff",
  borderRadius: 10,
  padding: "10px 12px",
  fontWeight: 900,
  cursor: "pointer",
};

const primaryButtonStyle = {
  border: "1px solid #2563eb",
  background: "#2563eb",
  color: "#fff",
  borderRadius: 10,
  padding: "10px 12px",
  fontWeight: 900,
  cursor: "pointer",
};

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 10,
};

const statusStyle = {
  padding: "6px 10px",
  borderRadius: 999,
  background: "#f8fafc",
  border: "1px solid #dbe4ee",
  fontSize: 11,
  fontWeight: 950,
};
